import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useStatsDossierProfitability } from '../../hooks/queries/useStats';
import { isAtRisk } from '../../services/stats/profitabilityHelpers';
import {
  DOSSIER_STAGE_LABELS,
  type DossierStage,
} from '../../services/dossiers/dossierService';

function stageLabel(s: string | null): string {
  if (!s) return '—';
  return DOSSIER_STAGE_LABELS[s as DossierStage] ?? s;
}

function formatEur(n: number | null): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n ?? 0);
}

function formatHours(n: number | null): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(n ?? 0);
}

export function ProfitabilityTable() {
  const navigate = useNavigate();
  const { data = [], isLoading, error } = useStatsDossierProfitability();
  const [showAtRiskOnly, setShowAtRiskOnly] = useState(false);

  const now = useMemo(() => new Date(), []);
  const enriched = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        atRisk: isAtRisk(
          {
            margin: Number(d.margin ?? 0),
            opened_at: d.opened_at,
            invoiced_ht: Number(d.invoiced_ht ?? 0),
          },
          now,
        ),
      })),
    [data, now],
  );

  const atRiskCount = useMemo(
    () => enriched.filter((d) => d.atRisk).length,
    [enriched],
  );

  const sorted = useMemo(
    () =>
      [...enriched]
        .sort((a, b) => Number(a.margin ?? 0) - Number(b.margin ?? 0))
        .filter((d) => !showAtRiskOnly || d.atRisk)
        .slice(0, 50),
    [enriched, showAtRiskOnly],
  );

  if (isLoading) {
    return <p className="text-sm text-cime-text-muted">Chargement…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-600">Erreur de chargement.</p>;
  }
  if (data.length === 0) {
    return <p className="text-sm text-cime-text-muted">Aucun dossier.</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setShowAtRiskOnly((v) => !v)}
          className={`flex items-center gap-1.5 text-sm font-medium rounded-md px-2.5 py-1 transition ${
            atRiskCount > 0
              ? showAtRiskOnly
                ? 'bg-red-600 text-white'
                : 'bg-red-50 text-red-700 hover:bg-red-100'
              : 'bg-neutral-100 text-cime-text-muted cursor-default'
          }`}
          disabled={atRiskCount === 0}
        >
          <AlertTriangle className="w-4 h-4" />
          {atRiskCount} dossier{atRiskCount > 1 ? 's' : ''} à surveiller
        </button>
        {showAtRiskOnly && (
          <button
            type="button"
            onClick={() => setShowAtRiskOnly(false)}
            className="text-xs text-cime-text-muted hover:underline"
          >
            Voir tous les dossiers
          </button>
        )}
      </div>

      <div className="bg-cime-surface border border-neutral-200 rounded-cime overflow-hidden">
        <table className="cime-table text-sm">
          <thead>
            <tr>
              <th className="text-left px-3 py-2.5">Numéro</th>
              <th className="text-left px-3 py-2.5">Intitulé</th>
              <th className="text-left px-3 py-2.5">Étape</th>
              <th className="text-right px-3 py-2.5">Heures facturables</th>
              <th className="text-right px-3 py-2.5">Valeur des heures</th>
              <th className="text-right px-3 py-2.5">Facturé HT</th>
              <th className="text-right px-3 py-2.5">Marge</th>
              <th className="text-left px-3 py-2.5">Statut</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr
                key={d.dossier_id}
                onClick={() => d.dossier_id && navigate(`/dossiers/${d.dossier_id}`)}
                className="cursor-pointer"
              >
                <td className="px-3 py-2.5 font-medium">{d.number ?? '—'}</td>
                <td className="px-3 py-2.5">{d.title ?? '—'}</td>
                <td className="px-3 py-2.5">{stageLabel(d.stage)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {formatHours(Number(d.hours_billable ?? 0))}
                </td>
                <td className="px-3 py-2.5 text-right cime-amount">
                  {formatEur(Number(d.hours_amount ?? 0))}
                </td>
                <td className="px-3 py-2.5 text-right cime-amount">
                  {formatEur(Number(d.invoiced_ht ?? 0))}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium">
                  <span
                    className="cime-amount"
                    style={{
                      color:
                        Number(d.margin ?? 0) < 0
                          ? 'var(--cime-urgent)'
                          : Number(d.margin ?? 0) > 0
                            ? 'var(--cime-success)'
                            : undefined,
                    }}
                  >
                    {formatEur(Number(d.margin ?? 0))}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  {d.atRisk && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded">
                      <AlertTriangle className="w-3 h-3" />
                      À surveiller
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
